import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, FormControlLabel, Switch, Typography, CircularProgress,
    MenuItem, InputAdornment
} from '@mui/material';
import { listModel, ModelKey } from '../../../config/aiConfig';
import MediaLibrary from '../../common/MediaLibrary';

interface CourseData {
    title: string;
    description: string;
    imageUrl: string;
    duration: string;
}

interface CourseModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CourseData) => void;
    onAiGenerate?: (data: { topic: string; description: string; instructions: string; modelChoose: string }) => Promise<void>;
    initialData?: Partial<CourseData>;
    isEditing?: boolean;
}

const CourseModal: React.FC<CourseModalProps> = ({ open, onClose, onSave, onAiGenerate, initialData, isEditing = false }) => {
    const [data, setData] = useState<CourseData & { status: 'publish' | 'draft' | 'removed' | 'imported' }>({
        title: '',
        description: '',
        imageUrl: '',
        duration: '',
        status: 'draft'
    });
    const [openMediaLibrary, setOpenMediaLibrary] = useState(false);
    const [titleError, setTitleError] = useState('');

    // AI Generation State
    const [isAiMode, setIsAiMode] = useState(false);
    const [aiTopic, setAiTopic] = useState('');
    const [aiDescription, setAiDescription] = useState('');
    const [aiInstructions, setAiInstructions] = useState('');
    const [generating, setGenerating] = useState(false);
    const [modelChoose, setModelChoose] = useState<ModelKey>('default');

    useEffect(() => {
        if (open) {
            if (initialData) {
                setData({
                    title: initialData.title || '',
                    description: initialData.description || '',
                    imageUrl: initialData.imageUrl || '',
                    duration: (initialData as any).duration || '',
                    status: (initialData as any).status || 'draft'
                });
            } else {
                setData({
                    title: '',
                    description: '',
                    imageUrl: '',
                    duration: '',
                    status: 'draft'
                });
            }
            setTitleError('');
            setIsAiMode(false);
            setAiTopic('');
            setAiDescription('');
            setAiInstructions('');
            setModelChoose('default');
        }
    }, [open, initialData]);

    const handleSave = () => {
        if (!data.title.trim()) {
            setTitleError('Le titre est requis');
            return;
        }

        const payload: any = { ...data };

        if (!isEditing) {
            // Default values for new courses required by the model
            payload.old_wordPressId = 0;
            payload.old_permalink = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
            payload.old_image = '';
            payload.lastActivity = new Date().toISOString();
        }

        onSave(payload);
        onClose();
    };

    const handleAiGenerate = async () => {
        if (!aiTopic.trim()) {
            setTitleError('Le sujet est requis');
            return;
        }

        if (onAiGenerate) {
            setGenerating(true);
            try {
                await onAiGenerate({
                    topic: aiTopic,
                    description: aiDescription,
                    instructions: aiInstructions,
                    modelChoose: modelChoose
                });
                onClose();
            } finally {
                setGenerating(false);
            }
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? 'Modifier le cours' : 'Ajouter un nouveau cours'}</DialogTitle>
                <DialogContent>
                    {!isEditing && (
                        <Box sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 3,
                            bgcolor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1
                        }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mode de création</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isAiMode ? "IA Générative" : "Manuelle"}
                                </Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isAiMode}
                                        onChange={(e) => setIsAiMode(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#667eea',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#667eea',
                                            },
                                        }}
                                    />
                                }
                                label=""
                            />
                        </Box>
                    )}

                    {isAiMode ? (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Sujet du cours (IA)"
                                fullWidth
                                required
                                value={aiTopic}
                                onChange={(e) => {
                                    setAiTopic(e.target.value);
                                    if (titleError) setTitleError('');
                                }}
                                error={!!titleError}
                                helperText={titleError}
                                placeholder="Ex: Introduction au Marketing Digital..."
                            />
                            <TextField
                                margin="dense"
                                label="Description du cours (IA)"
                                fullWidth
                                multiline
                                rows={3}
                                value={aiDescription}
                                onChange={(e) => setAiDescription(e.target.value)}
                                placeholder="Détaillez le contenu attendu pour l'IA..."
                            />
                            <TextField
                                margin="dense"
                                label="Instructions supplémentaires"
                                fullWidth
                                multiline
                                rows={2}
                                value={aiInstructions}
                                onChange={(e) => setAiInstructions(e.target.value)}
                                placeholder="Ex: Niveau débutant, ton formel..."
                            />
                            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.05)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                                <TextField
                                    select
                                    label="Modèle IA"
                                    fullWidth
                                    value={modelChoose}
                                    onChange={(e) => setModelChoose(e.target.value as ModelKey)}
                                    size="small"
                                >
                                    {(Object.keys(listModel) as ModelKey[]).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', gap: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{listModel[key].name}</Typography>
                                                        <Box component="span" sx={{
                                                            fontSize: '0.65rem',
                                                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                            color: '#667eea',
                                                            fontWeight: 600,
                                                            px: 0.8,
                                                            py: 0.2,
                                                            borderRadius: 1
                                                        }}>
                                                            {listModel[key].model}
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {listModel[key].description}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                                        <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>
                                                            ⏱ {listModel[key].time}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                            {[...Array(10)].map((_, i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        background: i < listModel[key].qualityScore
                                                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                            : 'rgba(0,0,0,0.1)',
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', whiteSpace: 'nowrap' }}>
                                                    {listModel[key].price}€
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(102, 126, 234, 0.08)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
                                                {listModel[modelChoose].name}
                                                <Box component="span" sx={{
                                                    ml: 1,
                                                    fontSize: '0.6rem',
                                                    bgcolor: '#667eea',
                                                    color: 'white',
                                                    px: 0.6,
                                                    py: 0.2,
                                                    borderRadius: 0.5
                                                }}>
                                                    {listModel[modelChoose].model}
                                                </Box>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                ⏱ {listModel[modelChoose].time} • Qualité: {listModel[modelChoose].qualityScore}/10
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea' }}>
                                            ~{listModel[modelChoose].price}€ / cours
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Titre du cours"
                                fullWidth
                                required
                                value={data.title}
                                onChange={(e) => {
                                    setData({ ...data, title: e.target.value });
                                    if (titleError) setTitleError('');
                                }}
                                error={!!titleError}
                                helperText={titleError}
                            />
                            <TextField
                                margin="dense"
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                value={data.description}
                                onChange={(e) => setData({ ...data, description: e.target.value })}
                            />
                            <TextField
                                margin="dense"
                                label="Durée indicative (ex: 2h 30min)"
                                fullWidth
                                value={data.duration}
                                onChange={(e) => setData({ ...data, duration: e.target.value })}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    margin="dense"
                                    label="Image URL"
                                    fullWidth
                                    value={data.imageUrl}
                                    onChange={(e) => setData({ ...data, imageUrl: e.target.value })}
                                />
                                <Button sx={{ mt: 1 }} variant="outlined" onClick={() => setOpenMediaLibrary(true)}>
                                    Select Image
                                </Button>
                            </Box>
                            <TextField
                                select
                                margin="dense"
                                label="Status"
                                fullWidth
                                value={data.status}
                                onChange={(e) => setData({ ...data, status: e.target.value as any })}
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                <option value="draft">Draft</option>
                                <option value="publish">Publish</option>
                                <option value="removed">Removed</option>
                                <option value="imported">Imported</option>
                            </TextField>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={generating}>Cancel</Button>
                    <Button
                        onClick={isAiMode ? handleAiGenerate : handleSave}
                        variant="contained"
                        disabled={generating}
                        sx={isAiMode ? {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontWeight: 700
                        } : {}}
                    >
                        {generating ? <CircularProgress size={24} color="inherit" /> : (isAiMode ? 'Générer avec IA' : (isEditing ? 'Save Changes' : 'Create Course'))}
                    </Button>
                </DialogActions>
            </Dialog>

            <MediaLibrary
                open={openMediaLibrary}
                onClose={() => setOpenMediaLibrary(false)}
                onSelect={(url) => setData({ ...data, imageUrl: url })}
            />
        </>
    );
};

export default CourseModal;